import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "@/locales/en.json";
import ar from "@/locales/ar.json";

const STORAGE_KEY = "spayzo.lang";

export type Lang = "en" | "ar";

export function readCookieLang(cookieHeader?: string | null): Lang {
  const source =
    cookieHeader ?? (typeof document !== "undefined" ? document.cookie : "");
  if (!source) return "en";
  const match = source.match(new RegExp(`(?:^|;\\s*)${STORAGE_KEY}=(en|ar)`));
  return match?.[1] === "ar" ? "ar" : "en";
}

// On the client the cookie is readable before hydration, so the first client
// render matches the server (which reads the same cookie per request).
// localStorage is only applied later, after the page has fully loaded.
function getInitialLang(): Lang {
  if (typeof document === "undefined") return "en";
  return readCookieLang();
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      ar: { translation: ar },
    },
    lng: getInitialLang(),
    fallbackLng: "en",
    interpolation: { escapeValue: false },
    returnEmptyString: false,
  });
}

export function applyDirection(lang: string) {
  if (typeof document === "undefined") return;
  const dir = lang === "ar" ? "rtl" : "ltr";
  document.documentElement.setAttribute("dir", dir);
  document.documentElement.setAttribute("lang", lang);
}

export function readStoredLang(): Lang {
  if (typeof window === "undefined") return "en";
  const cookieLang = readCookieLang();
  if (cookieLang === "ar") return "ar";
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "ar" ? "ar" : "en";
  } catch {
    return "en";
  }
}

export function setLanguage(lang: Lang) {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable */
    }
    const secure = window.location.protocol === "https:";
    document.cookie = `${STORAGE_KEY}=${lang};path=/;max-age=31536000;samesite=${
      secure ? "none;secure" : "lax"
    }`;
  }
  i18n.changeLanguage(lang);
  applyDirection(lang);
}


// Sync direction on load
if (typeof window !== "undefined") {
  applyDirection(i18n.language);
}

export default i18n;
